import type { ReactNode } from 'react';

export interface LegendItem {
  /** Optional section heading rendered above the first item of the section. */
  section?: string;
  /** Display label for the swatch. */
  label: ReactNode;
  /** Border colour for the swatch. */
  color?: string;
  /** Fill colour for the swatch. Falls back to `color`. */
  fillColor?: string;
  /**
   * Fill opacity, blended against the legend's white card so the swatch
   * matches how the layer actually paints. Needed by any legend whose layers
   * differ by opacity rather than hue — e.g. the parcel colour ramp, which is
   * one blue at four intensities and would otherwise show four identical
   * swatches. Omitted = fully opaque, as before.
   */
  fillOpacity?: number;
  /** Render the swatch border as a dashed outline (FPA boundary style). */
  dashed?: boolean;
}

interface MapLegendProps {
  /** Ordered list of swatches. Items with a new `section` automatically
   *  emit a sub-heading above themselves. */
  items: LegendItem[];
  /**
   * When MapCanvas mounts the legend itself it positions it absolutely. Use
   * this prop when you're rendering the legend yourself outside a map.
   */
  className?: string;
}

/** Blend a hex colour toward white at `alpha` — the legend card is white, so
 *  this reproduces on the swatch what the layer paints on a pale basemap. */
function blendOnWhite(hex: string, alpha: number): string {
  const m = /^#?([\da-f]{6})$/i.exec(hex.trim());
  if (!m || !m[1]) return hex;
  const n = parseInt(m[1], 16);
  const mix = (c: number) => Math.round(c * alpha + 255 * (1 - alpha));
  // eslint-disable-next-line no-bitwise
  return `rgb(${mix((n >> 16) & 255)}, ${mix((n >> 8) & 255)}, ${mix(n & 255)})`;
}

/**
 * Styled bottom-right map legend. Originally inlined in the legacy
 * `MapComponent.jsx`; pulled out so the unified `MapCanvas` can accept
 * either a raw `ReactNode` slot or this convenience component.
 */
export function MapLegend({ items, className }: MapLegendProps) {
  if (!items || items.length === 0) return null;

  let lastSection: string | undefined;

  return (
    <div
      className={className}
      style={{
        backgroundColor: 'white',
        padding: 8,
        borderRadius: 4,
        boxShadow: '0 1px 5px rgba(0,0,0,0.4)',
        minWidth: 140,
        maxWidth: 220,
      }}
    >
      {items.map((item, idx) => {
        const sectionChange = item.section && item.section !== lastSection;
        const swatchBorder = item.dashed
          ? `2px dashed ${item.color ?? '#888'}`
          : `1px solid ${item.color ?? '#888'}`;
        const rawFill = item.fillColor ?? item.color;
        const swatchFill =
          rawFill == null
            ? 'transparent'
            : item.fillOpacity == null
              ? rawFill
              : blendOnWhite(rawFill, item.fillOpacity);
        const node = (
          <div key={`row-${idx}`} style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
            <div
              style={{
                width: 16,
                height: 16,
                backgroundColor: swatchFill,
                marginRight: 8,
                border: swatchBorder,
              }}
            />
            <span style={{ fontSize: 11 }}>{item.label}</span>
          </div>
        );
        if (sectionChange) {
          const heading = (
            <div
              key={`sec-${idx}`}
              style={{
                fontSize: 12,
                fontWeight: 'bold',
                marginTop: lastSection ? 6 : 0,
                marginBottom: 5,
              }}
            >
              {item.section}
            </div>
          );
          lastSection = item.section;
          return (
            <div key={`block-${idx}`}>
              {heading}
              {node}
            </div>
          );
        }
        return node;
      })}
    </div>
  );
}

export default MapLegend;
